import { createGlobalStyle } from 'styled-components';
import { normalize } from 'styled-normalize';

import type { ResolvedTheme, ThemeDef } from '@typings/theme.ts';
import tageditorStyle from '@ui/tageditorStyle.ts';
import { rgba } from '@utils/dom.ts';
import { StateObjMultiple } from '@utils/stateObj.ts';
import { getTransitColors, type TransitMapTheme } from '@utils/transitColors.ts';

import { LoadingIndicator } from './LoadingIndicator.tsx';
import { getTheme } from './theme.ts';

/** Minimal app shape needed by themeManager */
interface ThemeManagerApp {
  bus: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bus handlers accept varying payload shapes
    on(event: string, handler: (payload: any) => unknown): () => void;
    get(event: string, payload?: unknown): Promise<unknown>;
  };
}

type ThemeManagerState = {
  theme: ResolvedTheme;
  contentURL: string | null;
};

/**
 * Generate CSS custom properties from theme colors
 * Flattens nested keyboard colors and creates alpha variants on-demand
 *
 * Alpha variants are defined explicitly based on actual usage in the codebase.
 * This approach:
 * - Makes CSS output more efficient (only generates what we use)
 * - Documents which alpha variants are actually needed
 * - Makes it easy to spot opportunities for consolidation
 *
 * To add a new alpha variant:
 * 1. Add it to the alphaVariants array below
 * 2. Use it in code as: var(--color-{colorName}-{alphaValue})
 */
const generateCSSVariables = (theme: ResolvedTheme) => {
  const cssVars: string[] = [];

  // Define only the alpha variants we actually use (discovered via: rg "var\(--color-[a-zA-Z]+-[0-9]{2}\)")
  // Format: { color: 'colorName', alpha: 0.05 } generates --color-colorName-05
  const alphaVariants = [
    // Primary colors with transparency
    { color: 'primary', alpha: 0.05 }, // Used in: ProgressiveImage loading background
    { color: 'primary', alpha: 0.1 }, // Used in: PoiFeedback hover/active states
    { color: 'primary', alpha: 0.2 }, // Used in: FlightSummary flight icon background
    { color: 'primary', alpha: 0.5 }, // Used in: styled-component-tools.js example/fallback

    // Primary button colors with transparency
    { color: 'primaryButton', alpha: 0.1 }, // Used in: StepView hover state
    { color: 'primaryButton', alpha: 0.15 }, // Used in: Bluedot accuracy circle
    { color: 'primaryButton', alpha: 0.3 }, // Used in: LanePickerView disabled button
    { color: 'primaryButton', alpha: 0.85 }, // Used in: Gallery controls, image number indicator
    { color: 'primaryButtonText', alpha: 0.4 }, // Used in: Button disabled states

    // Map navigation colors with transparency
    { color: 'mapNavBackground', alpha: 0.85 }, // Used in: Gallery controls, trigger buttons

    // Secondary colors with transparency
    { color: 'secondaryText', alpha: 0.4 }, // Used in: Button secondary text
    { color: 'secondaryText', alpha: 0.5 }, // Used in: FollowMeButton unfocused state
    { color: 'secondaryButtonText', alpha: 0.5 }, // Used in: Button states

    // Primary text with transparency
    { color: 'primaryText', alpha: 0.1 }, // Used in: Security lane active state
    { color: 'primaryText', alpha: 0.15 }, // Used in: OtherSecurityLanes border
    { color: 'primaryText', alpha: 0.7 }, // Used in: Flight list element strikethrough text

    // Secondary color with transparency
    { color: 'secondary', alpha: 0.1 }, // Used in: Security lane hover state

    // Widget colors with transparency
    { color: 'widgetText', alpha: 0.1 }, // Used in: Widget backgrounds
    { color: 'widgetText', alpha: 0.25 }, // Used in: Search input focus outline (hex 40 = 0.25)

    // Background colors with transparency
    { color: 'background', alpha: 0.5 }, // Used in: MapPanControlsDesktop overlay

    // Tooltip colors with transparency
    { color: 'toolTipText', alpha: 0.5 }, // Used in: ToastComponent text
  ];

  // Process all base colors
  for (const [colorName, colorValue] of Object.entries(theme.colors)) {
    if (colorName === 'keyboard') {
      // Flatten keyboard colors
      for (const [keyboardColorName, keyboardColorValue] of Object.entries(colorValue as Record<string, unknown>)) {
        if (typeof keyboardColorValue === 'string') {
          cssVars.push(`--color-keyboard-${keyboardColorName}: ${keyboardColorValue};`);
        }
      }
    } else if (typeof colorValue === 'string') {
      // Base color variable
      cssVars.push(`--color-${colorName}: ${colorValue};`);
    }
  }

  // Generate alpha variants
  for (const { color, alpha } of alphaVariants) {
    const colorValue = theme.colors[color];
    if (colorValue && typeof colorValue === 'string') {
      const alphaStr = String(Math.round(alpha * 100)).padStart(2, '0');
      const rgbaValue = rgba(colorValue, alpha);
      cssVars.push(`--color-${color}-${alphaStr}: ${rgbaValue};`);
    }
  }

  // Composite focus outline shorthand using the resolved focus outline color
  cssVars.push(`--focus-outline: 2px solid ${theme.colors.focusOutlineColor};`);

  return cssVars.join('\n    ');
};

const GlobalStyle = createGlobalStyle`
  .atrius-map
  {
    /* CSS Custom Properties (Variables) for theme colors */
    ${props => generateCSSVariables(props.theme)}

    ${normalize}
    ${tageditorStyle}

    box-sizing: border-box;
    font-size: 100%;
    touch-action: manipulation;

    /* Anchor for position:absolute overlays portaled into this element
       (e.g. mobile user menu) - avoids relying on position:fixed viewport math. */
    position: relative;

    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primaryText};
    font-family: ${props => props.theme.fonts.family};
    font-weight: normal;
    line-height: 1.5;
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overscroll-behavior-y: none;
    text-align: start;

  /*
      The following rule mostly fixes the secondary rubber banding issue
      (see https://stackoverflow.com/questions/37586286/prevent-overflow-rubberband-scrolling-on-ios)
      but it does so via fixed positioning - which would effect usage in SDK, etc - so needs
      to be carefully considered. So we are commening it out for now.

    @supports (-webkit-touch-callout: none) {
      position: fixed;
      width: 100vw;
      height: -webkit-fill-available;
    }
  */

    .maplibregl-canvas-container canvas { left: 0; top: 0 }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }

    h1, h2, h3, h4, h5, h6, p {
      font-family: ${props => props.theme.fonts.family};
      font-style: normal;
      font-weight: normal;
    }

    h1 { ${({ theme }) => theme.fontSize('h1')}; }
    h2 { ${({ theme }) => theme.fontSize('h2')}; }
    h3 { ${({ theme }) => theme.fontSize('h3')}; }
    h4 { ${({ theme }) => theme.fontSize('h4')}; }
    h5 { ${({ theme }) => theme.fontSize('h5')}; }
    h6 { ${({ theme }) => theme.fontSize('h6')}; }
    p { ${({ theme }) => theme.fontSize('h3')}; }

    @media print, screen and (min-width: 40em) {
      h1 { ${({ theme }) => theme.fontSize('26px')}; }
      h2 { ${({ theme }) => theme.fontSize('20px')}; }
      h3 { ${({ theme }) => theme.fontSize('18px')}; }
      h4 { ${({ theme }) => theme.fontSize('16px')}; }
      h5 { ${({ theme }) => theme.fontSize('26px')}; text-transform: uppercase; letter-spacing: 0.5rem; }
      h6 { ${({ theme }) => theme.fontSize('12px')}; }
      p { ${({ theme }) => theme.fontSize('18px')}; }
    }

    .nopointer { pointer-events: none; }

    .MarkerPopup > div {
      border-radius: 8px;
      max-width: 480px;
    }
  }

  /* Default keyboard-focus outline for focusable elements in the map. Kept at low
     specificity (:where) so any component can override it with its own focus style. */
  :where(.atrius-map) :focus-visible {
    outline: var(--focus-outline);
  }
`;

const initThemeManager = (app: ThemeManagerApp) => {
  const themeState = new StateObjMultiple<ThemeManagerState>({
    theme: {} as ResolvedTheme,
    contentURL: null,
  });

  app.bus.on('venueData/mapDataLoaded', ({ mapTheme }: { mapTheme: TransitMapTheme }) => {
    const theme = themeState.getState().theme;
    const { train, bus } = getTransitColors(mapTheme);
    themeState.update({
      theme: {
        ...theme,
        colors: {
          ...theme.colors,
          categoryBadgeWayfindingTransit: train,
          categoryBadgeWayfindingTrain: train,
          categoryBadgeWayfindingBus: bus,
        },
      },
    });
  });

  app.bus.on('venueData/venueDataLoaded', () =>
    app.bus
      .get('venueData/getContentUrl', { type: 'web-engine-ui' })
      .then((url: unknown) => themeState.update({ contentURL: url as string })),
  );

  app.bus.on('theme/getVenueLogo', () =>
    app.bus.get('venueData/getContentUrl', {
      type: 'spritesheet-svg',
      name: `venue-logo.svg`,
    }),
  );

  app.bus.on('debugTools/fileDrop', async ({ file, content }: { file: { type: string }; content: string }) => {
    const theme = themeState.getState().theme;
    if (file.type === 'application/json') {
      const jsonOb = JSON.parse(content);
      if (jsonOb.theme) {
        for (const cn in jsonOb.theme.colors) {
          theme.colors[cn] = jsonOb.theme.colors[cn];
        }
      }
    }
  });

  const updateTheme = (themeDef: ThemeDef, defaultTheme?: string) => {
    const theme = getTheme(themeDef, defaultTheme);
    theme.getBaseUIUrl = () => themeState.getState().contentURL ?? '';
    themeState.update({ theme });
  };

  const buildTheme = (themeDef: ThemeDef, defaultTheme?: string) => {
    updateTheme(themeDef, defaultTheme);
    return {
      getTheme: () => themeState.getState().theme,
      GlobalStyle,
      Loading: LoadingIndicator,
      updateTheme,
      getThemeState: () => themeState,
    };
  };

  return { buildTheme };
};

export { initThemeManager, GlobalStyle, generateCSSVariables };
